import { reactive } from "vue";

import { ApiError, call, refreshCsrf, request } from "./client";

export const session = reactive({
	user: "Guest",
	fullName: "Guest",
	role: "Consultant",
	roles: [],
	loaded: false,
});

let loading = null;

export async function loadSession() {
	if (loading) return loading;
	loading = loadSessionNow().finally(() => {
		loading = null;
	});
	return loading;
}

async function loadSessionNow() {
	const clientCode = (localStorage.getItem("clientCode") || "").trim();
	if (!clientCode) {
		applyGuest();
		return session;
	}

	try {
		const logged = await call("frappe.auth.get_logged_user", {}, {
			httpMethod: "GET",
			skipAuthRedirect: true,
		});

		if (!logged || logged === "Guest") {
			applyGuest();
			return session;
		}

		let info = { user: logged, full_name: logged, roles: [] };
		try {
			info = (await call("tripplan.api.auth.get_session", {}, { skipAuthRedirect: true })) || info;
		} catch {
			/* older sites still have a session from /api/method/login */
		}
		applyUser(info);
	} catch {
		applyGuest();
	}

	return session;
}

export async function login(usr, pwd) {
	const payload = await request("login", { usr, pwd }, { skipAuthRedirect: true });
	const message = payload?.message;
	if (message !== "Logged In" && message !== "No App") {
		throw new ApiError(typeof message === "string" ? message : "Login failed", 401);
	}

	try {
		await refreshCsrf();
	} catch {
		/* the next write will ask for a token again */
	}

	session.loaded = false;
	await loadSession();
	if (session.user === "Guest") {
		throw new ApiError("Login succeeded, but the session could not be read", 401);
	}
	return session;
}

export async function registerAccount({ fullName, email, password }) {
	try {
		await refreshCsrf();
	} catch {
		/* guest CSRF is fetched again on the retry inside request() */
	}

	await call(
		"tripplan.api.auth.register",
		{ full_name: fullName, email, password },
		{ skipAuthRedirect: true },
	);
	return login(email, password);
}

export async function logout() {
	try {
		await request("logout", {}, { httpMethod: "GET", skipAuthRedirect: true });
	} catch {
		/* clear the local session even if the site is already gone */
	}
	localStorage.removeItem("csrfToken");
	localStorage.removeItem("Userdetails");
	applyGuest();
}

function applyUser(info) {
	session.user = info.user || info.email || "Guest";
	session.fullName = info.full_name || session.user;
	session.roles = info.roles || [];
	session.role = pickRole(session.roles);
	session.loaded = true;
	localStorage.setItem(
		"Userdetails",
		JSON.stringify({
			id: session.user,
			full_name: session.fullName,
			email: info.email || session.user,
			roles: session.roles,
		}),
	);
}

function applyGuest() {
	session.user = "Guest";
	session.fullName = "Guest";
	session.role = "Consultant";
	session.roles = [];
	session.loaded = true;
	localStorage.removeItem("Userdetails");
}

function pickRole(roles) {
	if (roles.includes("System Manager")) return "Admin";
	if (roles.includes("Accounts User")) return "Accounts";
	if (roles.includes("Sales User")) return "Consultant";
	return "User";
}

export function initials(name) {
	return (name || "U")
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0].toUpperCase())
		.join("");
}
